import { Dispatch, useEffect, useRef, useState } from 'react';
import { useIdleTimer } from 'react-idle-timer';
import { redirectUserToLogoutScreen, refreshUserSession } from '../store/action/auth';

export function useInterval(callback: () => void, delay: number) {
  const savedCallback = useRef<() => void>(() => {});

  // Remember the latest callback.
  useEffect(() => {
    savedCallback.current = callback;
  }, [callback]);

  // Set up the interval.
  useEffect(() => {
    function tick() {
      savedCallback.current();
    }
    const id = setInterval(tick, delay);
    return () => clearInterval(id);
  }, [delay]);
}

export const useRefreshTimer = (
  dispatch: Dispatch<unknown>,
  refreshTimeout: number,
  idleTimeout: number,
  timerInterval: number,
  enabled: boolean,
) => {
  const [sessionStartTime, setSessionStartTime] = useState(Date.now());

  const onIdle = () => {
    if (!enabled) return;
    redirectUserToLogoutScreen();
  };

  const onMessage = (newSessionStart: number) => {
    if (!enabled) return;
    setSessionStartTime(newSessionStart);
  };

  const { getLastActiveTime, isLastActiveTab, message } = useIdleTimer({
    timeout: idleTimeout,
    onIdle,
    onMessage,
    debounce: 250,
    crossTab: true,
    syncTimers: 1000,
  });

  useInterval(() => {
    if (!enabled) return;

    const now = Date.now();
    const activeFor = now - sessionStartTime;
    const lastActiveMS = getLastActiveTime()?.getMilliseconds() || 0;

    if (activeFor > refreshTimeout && lastActiveMS < refreshTimeout && isLastActiveTab()) {
      refreshUserSession(dispatch).then(authenticationSuccessful => {
        if (authenticationSuccessful) {
          setSessionStartTime(now);
          message(now, false);
        }
      });
    }
  }, timerInterval);

  return { sessionStartTime };
};
